import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import { ZONES } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from "../models/ColumnDefinitions/ZoneColumns";
import { Lookup } from "../models/Lookup";
import ControllerSearch from "../models/search/ControllerSearch";
import SiteSearch from "../models/search/SiteSearch";
import searchColumns from "../models/search/ZoneSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getControllerNames } from "../services/controllerService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { deleteZone, getZones } from "../services/zoneService";
import LookupTypes from "../models/LookupTypes";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange: (filters: any) => void
}

const ZonesTab: FC<Props> = ({ setBusy, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.zones);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const itemPath = "/access-control/hardware/zone/";

    useEffect(() => {
        setLookup([{
            name: LookupTypes.zoneBySiteId,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.controller,
            values: [],
            dataMethod: getControllerNames,
            searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, []);

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteZone(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))

    const initialiseUserPreferences = (setDetails: (userLoginId, ZONES) => void) => {
        setDetails(userLoginId, ZONES);
    }
    // <-- end context

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={getZones}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        deleteMethod={onDelete}
        lookup={lookup}
        customDelete={{ fieldName: "isDefault", comparisonValue: false }} >
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath} 
            cyName="zones"/>
    </TableContextProvider>
};

export default ZonesTab;
